import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { auth, db, doc, getDoc } from '../lib/firebase';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  userProfile: UserProfile | null;
  error: string | null;
}

interface UserProfile {
  email: string;
  agencyId: string;
  role: 'admin' | 'user';
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  permissions?: {
    collaborations: boolean;
    todo: boolean;
    clients: boolean;
    influencers: boolean;
    casting: boolean;
    accounting: boolean;
    resources: boolean;
    assistant: boolean;
  };
}

const AuthContext = createContext<AuthContextType>({ 
  currentUser: null, 
  loading: true,
  userProfile: null,
  error: null
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      try {
        setCurrentUser(user);
        setError(null);
        
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data() as UserProfile);
          } else {
            setUserProfile(null);
          }
        } else {
          setUserProfile(null);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        // Don't set error for auth state changes, just log it
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{ currentUser, loading, userProfile, error }}>
      {children}
    </AuthContext.Provider>
  );
}